import { startOfDay } from 'date-fns'

export interface TimeSlot {
  time: string
  hour: number
  minute: number
  available: boolean
}

// A slot must start at least this far in the future to be bookable.
export const BOOKING_LEAD_TIME_MS = 4 * 60 * 60 * 1000

// Generate time slots for a day
export const generateTimeSlots = (): { morning: TimeSlot[], afternoon: TimeSlot[], lateAfternoon: TimeSlot[] } => {
  const slots = {
    morning: [] as TimeSlot[],
    afternoon: [] as TimeSlot[],
    lateAfternoon: [] as TimeSlot[]
  }

  // Morning slots: 9:00 AM - 11:30 AM
  for (let hour = 9; hour < 12; hour++) {
    for (const minute of [0, 30]) {
      const time = `${hour}:${minute.toString().padStart(2, '0')} AM`
      slots.morning.push({ time, hour, minute, available: true })
    }
  }

  // Afternoon slots: 12:00 PM - 2:30 PM
  for (let hour = 12; hour < 15; hour++) {
    for (const minute of [0, 30]) {
      const displayHour = hour > 12 ? hour - 12 : hour
      const time = `${displayHour}:${minute.toString().padStart(2, '0')} PM`
      slots.afternoon.push({ time, hour, minute, available: true })
    }
  }

  // Late afternoon slots: 3:00 PM - 4:30 PM
  for (let hour = 15; hour < 17; hour++) {
    for (const minute of [0, 30]) {
      const displayHour = hour > 12 ? hour - 12 : hour
      const time = `${displayHour}:${minute.toString().padStart(2, '0')} PM`
      slots.lateAfternoon.push({ time, hour, minute, available: true })
    }
  }

  return slots
}

// Resolve a slot to its absolute start time on a given calendar day.
export const slotDateTime = (date: Date, slot: Pick<TimeSlot, 'hour' | 'minute'>): Date => {
  const start = startOfDay(date)
  start.setHours(slot.hour, slot.minute, 0, 0)
  return start
}

// A slot is bookable only if it starts at least the lead time from now. Comparing
// absolute datetimes (rather than hour-of-day) keeps this correct when the cutoff
// crosses midnight — e.g. late on a Friday, none of Friday's slots qualify.
export const isSlotBookable = (date: Date, slot: Pick<TimeSlot, 'hour' | 'minute'>, now: Date = new Date()): boolean => {
  return slotDateTime(date, slot).getTime() >= now.getTime() + BOOKING_LEAD_TIME_MS
}

// Utility function to check if a date has any available time slots
export const hasAvailableSlots = (date: Date, now: Date = new Date()): boolean => {
  const { morning, afternoon, lateAfternoon } = generateTimeSlots()

  return [...morning, ...afternoon, ...lateAfternoon].some((slot) => isSlotBookable(date, slot, now))
}
